/**
 * IntersectionObserver-based visibility tracking for Gutenberg blocks.
 * Fires fhs-component-viewed when a component becomes ≥50% visible in viewport
 * for the first time per page view.
 *
 * Expects: window.fhsAnalytics (from analytics.js) and elements with
 * data-fhs-component-type, data-fhs-component-id, data-fhs-pattern-id
 */

import { getDeviceType } from './getDeviceType.js';

const THRESHOLD = 0.5;
const SELECTOR = '[data-fhs-component-type]';
const EVENT_NAME = 'fhs-component-viewed';

/** Components that have already fired to prevent duplicate events */
const firedSet = new Set();

/**
 * Fire fhs-component-viewed for a component element. Tracks by component_id to ensure once per page.
 * @param {Element} el - Element with data-fhs-component-type, data-fhs-component-id, data-fhs-pattern-id
 * @returns {boolean} True if the event was successfully fired; false if it returned early (e.g. analytics unavailable).
 */
function fireComponentViewed(el) {
  const componentId = el.getAttribute('data-fhs-component-id');
  if (!componentId || firedSet.has(componentId)) return false;

  const track = window.fhsAnalytics?.track;
  if (typeof track !== 'function') return false;

  firedSet.add(componentId);

  const componentType = el.getAttribute('data-fhs-component-type') || '';
  const patternId = el.getAttribute('data-fhs-pattern-id') || undefined;

  track(EVENT_NAME, {
    component_type: componentType,
    component_id: componentId,
    pattern_id: patternId,
    device_type: getDeviceType(),
  });
  return true;
}

/**
 * Set up a single IntersectionObserver for all trackable components.
 * Handles dynamic content by observing a container and re-querying for new elements.
 */
function init() {
  if (typeof document === 'undefined' || !document.body) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= THRESHOLD) {
          if (fireComponentViewed(entry.target)) {
            observer.unobserve(entry.target);
          }
        }
      }
    },
    { threshold: THRESHOLD, rootMargin: '0px' }
  );

  function observeElements() {
    const elements = document.querySelectorAll(SELECTOR);
    for (const el of elements) {
      const componentId = el.getAttribute('data-fhs-component-id');
      if (componentId && !firedSet.has(componentId)) {
        observer.observe(el);
      }
    }
  }

  // Initial observe
  observeElements();

  // Handle dynamically loaded content
  let observeTimeout = null;
  const mo = new MutationObserver(() => {
    if (observeTimeout) clearTimeout(observeTimeout);
    observeTimeout = setTimeout(observeElements, 100);
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
